import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";
import type { SWRConfiguration } from "swr";

import type { GetTrialInstitutionSupportingSiteIdentityByIdResponse } from "src/api/Tmd.dtos";
import TrialInstitutionSupportingSiteIdentityContext from "src/contexts/data/TrialInstitutionSupportingSiteIdentityContext";
import useTrialInstitutionSupportingSiteIdentityById from "src/hooks/data/load/useTrialInstitutionSupportingSiteIdentityById";

/**
 * Configures the supporting site identity request and the descendants receiving its state.
 */
interface ITrialInstitutionSupportingSiteProviderProps extends PropsWithChildren {
    id?: number;
    configuration?: SWRConfiguration<
        GetTrialInstitutionSupportingSiteIdentityByIdResponse,
        GetTrialInstitutionSupportingSiteIdentityByIdResponse
    >;
}

/**
 * Publishes the supporting site identity loader state to descendants.
 *
 * Postconditions: Children receive the current loader result, including loading and error state;
 * rendering this provider does not guarantee a completed request. Children remain rendered while
 * loading or on failure. The supplied ID and SWR configuration pass through unchanged.
 */
const TrialInstitutionSupportingSiteIdentityProvider: FC<
    ITrialInstitutionSupportingSiteProviderProps
> = ({ children, id, configuration }) => {
    const context = useTrialInstitutionSupportingSiteIdentityById(id, configuration);

    return (
        <TrialInstitutionSupportingSiteIdentityContext.Provider value={context}>
            {children}
        </TrialInstitutionSupportingSiteIdentityContext.Provider>
    );
};

/**
 * Loads supporting site identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RouteTrialInstitutionSupportingSiteIdentityProvider: FC<PropsWithChildren> = ({
    children
}) => {
    const { supportingSiteId } = useParams<{ supportingSiteId: string }>();
    const id = supportingSiteId ? parseInt(supportingSiteId, 10) : undefined;

    return (
        <TrialInstitutionSupportingSiteIdentityProvider id={id}>
            {children}
        </TrialInstitutionSupportingSiteIdentityProvider>
    );
};

export default TrialInstitutionSupportingSiteIdentityProvider;
